import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import { openCustomerServiceChat } from '@/util/common';
import { usePermissions } from '@/util/hooks/checkPermissions';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getCustomerServiceHoursDisplay } from '@/util/customerServiceHours';
import { Tooltip } from 'antd';

const FLOATING_GAP = 30;
const DRAG_TRIGGER_DISTANCE = 4;

type FloatingPosition = {
  right: number;
  bottom: number;
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  startRight: number;
  startBottom: number;
  hasMoved: boolean;
};

type CustomerServiceCountrySnapshot = {
  country?: string;
  countryCode?: string;
};

type CustomerServiceReduxState = {
  User?: {
    data?: CustomerServiceCountrySnapshot[];
    selectedAddress?: CustomerServiceCountrySnapshot;
  };
};

const CustomerService = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [position, setPosition] = useState<FloatingPosition>({
    right: FLOATING_GAP,
    bottom: FLOATING_GAP,
  });
  const [isDragging, setIsDragging] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState>({
    pointerId: -1,
    startX: 0,
    startY: 0,
    startRight: FLOATING_GAP,
    startBottom: FLOATING_GAP,
    hasMoved: false,
  });
  const { checkPermission } = usePermissions();
  const { i18n } = useTranslation();
  const canShowCustomerService = checkPermission('S_showCustomerService');
  const customerCountryCode = useSelector((state: CustomerServiceReduxState) => {
    const currentUser = state.User?.data?.[0];
    const selectedAddress = state.User?.selectedAddress;
    return currentUser?.countryCode || currentUser?.country || selectedAddress?.countryCode || selectedAddress?.country;
  });
  const customerServiceHours = getCustomerServiceHoursDisplay(customerCountryCode, i18n.language);

  const getClampedPosition = useCallback((nextRight: number, nextBottom: number): FloatingPosition => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    const width = rect?.width || 56;
    const height = rect?.height || 56;
    const maxRight = Math.max(FLOATING_GAP, window.innerWidth - width - FLOATING_GAP);
    const maxBottom = Math.max(FLOATING_GAP, window.innerHeight - height - FLOATING_GAP);

    return {
      right: Math.min(Math.max(nextRight, FLOATING_GAP), maxRight),
      bottom: Math.min(Math.max(nextBottom, FLOATING_GAP), maxBottom),
    };
  }, []);

  useEffect(() => {
    if (!canShowCustomerService) {
      return undefined;
    }

    const handleResize = () => {
      setPosition(({ right, bottom }) => getClampedPosition(right, bottom));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [canShowCustomerService, getClampedPosition]);

  if (!canShowCustomerService) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (dragStateRef.current.hasMoved) {
      // 拖动结束会触发一次 click，这里拦截，避免用户只是移动客服入口却打开客服窗口。
      event.preventDefault();
      event.stopPropagation();
      dragStateRef.current.hasMoved = false;
      return;
    }

    openCustomerServiceChat();
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) {
      return;
    }

    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startRight: window.innerWidth - rect.right,
      startBottom: window.innerHeight - rect.bottom,
      hasMoved: false,
    };
    setIsDragging(true);
    setIsTooltipOpen(false);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!isDragging || dragState.pointerId !== event.pointerId) {
      return;
    }

    const offsetX = event.clientX - dragState.startX;
    const offsetY = event.clientY - dragState.startY;
    const moveDistance = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
    if (moveDistance < DRAG_TRIGGER_DISTANCE && !dragState.hasMoved) {
      return;
    }

    dragState.hasMoved = true;
    event.preventDefault();
    setPosition(getClampedPosition(dragState.startRight - offsetX, dragState.startBottom - offsetY));
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (dragStateRef.current.hasMoved) {
      // 正常浏览器会在 pointerup 后派发 click；定时兜底防止取消拖拽后影响下一次点击。
      window.setTimeout(() => {
        dragStateRef.current.hasMoved = false;
      }, 0);
    }
  };
  const customerServiceHoursTooltip = customerServiceHours ? (
    <div className={styles.customTooltip}>
      <div className={styles.tooltipIconWrap}>
        <i className={`${styles.tooltipIcon} iconFont icon-customer`} />
      </div>
      <div className={styles.tooltipContent}>
        <div className={styles.tooltipTitle}>{customerServiceHours.customerServiceText}</div>
        <div className={styles.tooltipLabel}>{customerServiceHours.serviceTimeText}</div>
        <div className={styles.tooltipLine}>{customerServiceHours.weekdayText}</div>
        {customerServiceHours.timeRanges.map(timeRange => (
          <div className={styles.tooltipLine} key={timeRange}>
            {timeRange}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div ref={wrapperRef} className={styles.serviceContainerWrapper} style={position}>
      <Tooltip
        title={customerServiceHoursTooltip}
        placement="left"
        autoAdjustOverflow
        open={isDragging ? false : undefined}
        onOpenChange={setIsTooltipOpen}
        overlayClassName={styles.customerServiceTooltip}
        arrow={false}
        destroyTooltipOnHide
      >
        <button
          type="button"
          className={`${styles.serviceContainer} ${isTooltipOpen ? styles.serviceContainerActive : ''} ${
            isDragging ? styles.serviceContainerDragging : ''
          }`}
          aria-label={customerServiceHours?.customerServiceText || TLT('客服')}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
        >
          {/* <img
            className={styles.serviceIcon}
            src="https://jdios.jdindustry.com/mubla1/98154601309801723.png"
            alt="Customer Service"
          /> */}
          <i className={`${styles.serviceIcon} iconFont icon-customer`} />
        </button>
      </Tooltip>
    </div>
  );
};

export default CustomerService;
